import { CellContext } from "@tanstack/react-table"
import { useContext, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { TeamRole } from "entities/team-members/types/TeamRole"
import { Team } from "entities/teams/types/Team"
import { useTeamActions } from "features/team-management/model/useTeamActions"

import { TeamListTableFeatures } from "./TeamListTableFeatures"
import { TeamListViewContext } from "./TeamListViewContext"

export function TeamListActionCell({
  info
}: {
  info: CellContext<TeamListTableFeatures, Team, Team>
}) {
  const { t } = useTranslation()
  const { userID, handlers } = useContext(TeamListViewContext)

  const roleInTeam = useMemo(() => {
    const value = info.getValue().memberships
    if (userID && value) {
      return value.find(member => member.user?.id === userID)?.role || null
    }
    return null
  }, [userID, info])

  const isAdmin = roleInTeam === TeamRole.ADMIN

  const { actions } = useTeamActions({
    teamID: info.getValue().id,
    idPrefix: "team-list",
    isReadonly: !isAdmin,
    ...handlers
  })

  return (
    <Button
      variant="simple"
      className="team-list__cell-content team-list__action-button"
      disabled={!isAdmin || actions.length === 0}
      // The only child is an icon, so Button's label -> aria-label forwarding
      // is the only accessible name available.
      label={t("Team.Actions.Actions")}
    >
      <Icon name={IconName.KEBAB} />
    </Button>
  )
}
